import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { selectEngine } from "@rista/payments/engine/session";
import { SESSION } from "@rista/payments/engine/machine";
import { toSaleLeg } from "@rista/payments/domains/sale";

export interface CustomerDisplayState {
  campaignData: any;
  paymentDetails: any;
  canCustomerEnterNumber: boolean;
  isPaymentInProcess: boolean;
  enteredPhoneNumber: string;
  /**
   * `Date.now()` of the last sale closed on this terminal, 0 before the first one.
   *
   * The display cannot infer this from the sale it is showing: checkout success resets the cart
   * in the same tick, so by the time anything re-renders the closed sale is gone. This is the
   * only record that it happened, and it is a timestamp so a display that mounts late shows
   * only what is left of the hold window rather than a full one.
   */
  saleClosedAt: number;
}

const initialState: CustomerDisplayState = {
  campaignData: null,
  paymentDetails: null,
  canCustomerEnterNumber: false,
  isPaymentInProcess: false,
  enteredPhoneNumber: "",
  saleClosedAt: 0,
};

export const customerDisplaySlice = createSlice({
  name: "customerDisplay",
  initialState,
  reducers: {
    getCustomerDisplayCampaign: (
      state,
      action: PayloadAction<{ customerDisplayId: string; callback?: any }>,
    ) => {
      // Saga handles this action.
    },
    setCustomerDisplayCampaign: (state, action: PayloadAction<any>) => {
      state.campaignData = action.payload;
    },
    setSalePaymentDetails: (state, action: PayloadAction<any>) => {
      state.paymentDetails = action.payload;
    },
    askCustomerPhoneNumber: (state, action: PayloadAction<any>) => {
      state.canCustomerEnterNumber = action.payload;
    },
    setCustomerEnteredNumber: (state, action: PayloadAction<string>) => {
      state.enteredPhoneNumber = action.payload;
    },
    ispaymentInProcess: (state, action: PayloadAction<boolean>) => {
      state.isPaymentInProcess = action.payload;
    },
    /**
     * Dispatched the moment a checkout succeeds, before the cart is reset — see saleClosedAt.
     * The timestamp is stamped in `prepare` rather than the reducer so the reducer stays pure.
     */
    saleClosedOnDisplay: {
      reducer: (state, action: PayloadAction<number>) => {
        state.saleClosedAt = action.payload;
      },
      prepare: () => ({ payload: Date.now() }),
    },
  },
});

export const {
  getCustomerDisplayCampaign,
  setCustomerDisplayCampaign,
  setSalePaymentDetails,
  askCustomerPhoneNumber,
  setCustomerEnteredNumber,
  ispaymentInProcess,
  saleClosedOnDisplay,
} = customerDisplaySlice.actions;
export default customerDisplaySlice.reducer;

/**
 * The payments the display shows, and the QR it puts in front of the customer.
 *
 * v1's checkout STORES them: it posts its own leg map here (`CheckoutContainer:322`). The
 * payment engine keeps its legs in its own slice, so while an engine session is open they are
 * read from there: in the sale's wire shape, the shape v1's map holds, and without a SPLIT draft,
 * which v1's map gets only once it is added. READ rather than copied in: v1's own hook still runs
 * above the engine's switch and posts an empty map as the engine opens, and a copy would race it
 * (docs/flows/PAYMENT_ENGINE.md §36 G6).
 */
export const selectPaymentDetails = createSelector(
  [
    (state: any) => selectEngine(state)?.status,
    (state: any) => selectEngine(state)?.legs,
    (state: any) => state?.customerDisplayStore?.paymentDetails,
  ],
  (status, legs, stored) =>
    status && status !== SESSION.IDLE
      ? new Map((legs ?? []).filter((leg) => !leg.draft).map((leg) => [leg.paymentId, toSaleLeg(leg)]))
      : stored
);
